import { parseLanguages } from 'core/lite/parsers';
import { compareField } from 'core/utils/utils';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { put, takeLatest } from 'redux-saga/effects';
import { getLangs, getLangScopes } from '../crud/langs.crud';
import { PERSIST_LANGS, REDUCER_LANGS } from './conf';

export const actionTypes = {
  GetLangs: 'cms/GET_LANGS',
  SetLangs: 'cms/SET_LANGS',
  GetLangScopes: 'cms/GET_LANG_SCOPES',
  SetLangScopes: 'cms/SET_LANG_SCOPES',
};

const initialState = {
  langs: [],
  scopes: [],
  loading: false,
  loaded: false,
};

// OJO con el `whitelist: ['data']`: en este estado NO existe ninguna clave `data`
// (las claves son `langs`, `scopes`, `loading` y `loaded`), así que redux-persist
// no persiste nada de este duck. Tras cada recarga `langs` arranca vacío y hay que
// esperar la respuesta de `GET /langs` (que se dispara al autenticar, ver
// `src/app/router/Routes.js`) antes de decidir nada sobre la lista: no se debe asumir
// que hay estado persistido. Por eso el consumidor necesita `loading`/`loaded` y no
// puede interpretar `langs: []` como "esta editorial solo tiene un idioma".
export const reducer = persistReducer({ storage, key: PERSIST_LANGS, whitelist: ['data'] }, (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.GetLangs:
      return { ...state, loading: true };

    case actionTypes.SetLangs: {
      const { response, isFilter } = action.payload;
      // `?? []` porque `parseLanguages` sólo aplica su valor por defecto con `undefined`:
      // una respuesta con `data: null` llegaría aquí como null y rompería su filter/map.
      let languages = parseLanguages(response?.data ?? [], isFilter);
      languages.sort((a, b) => {
        return compareField(a, b, 'description');
      });
      if (response && response.status === 'success' && response.data) {
        return { ...state, langs: languages, loading: false, loaded: true };
      }
      // La petición terminó (aunque fuese en error): `loaded` marca que ya hay respuesta
      // y `langs` se queda como estaba.
      return { ...state, loading: false, loaded: true };
    }

    case actionTypes.SetLangScopes: {
      const data = action.payload?.data ? action.payload.data : [];
      return { ...state, scopes: data };
    }

    default:
      return state;
  }
});

export const selectors = {
  getLangs: (state) => {
    return state.entities && state.entities[REDUCER_LANGS] ? state.entities[REDUCER_LANGS].langs : null;
  },
  getLangScopes: (state) => {
    return state.entities && state.entities[REDUCER_LANGS] ? state.entities[REDUCER_LANGS].scopes : null;
  },
  // true mientras la petición está en vuelo (entre `GetLangs` y `SetLangs`).
  getLangsLoading: (state) => state?.entities?.[REDUCER_LANGS]?.loading ?? false,
  // true en cuanto ha llegado una respuesta de `GET /langs`, con éxito o con error.
  // Es lo que distingue "todavía no sabemos qué idiomas hay" de "ya sabemos que hay 0 o 1":
  // con `loading` a solas no se puede, porque antes de disparar la petición también vale false.
  getIsLangsLoaded: (state) => state?.entities?.[REDUCER_LANGS]?.loaded ?? false,
};

export const actions = {
  getLangs: (params) => ({ type: actionTypes.GetLangs, payload: { ...params } }),
  fulfillLangs: (response, isFilter) => ({ type: actionTypes.SetLangs, payload: { response, isFilter } }),
  getLangScopes: (params) => ({ type: actionTypes.GetLangScopes, payload: params }),
  fulfillLangScopes: (response) => ({ type: actionTypes.SetLangScopes, payload: response }),
};

export function* saga() {
  yield takeLatest(actionTypes.GetLangs, function* getLangsSaga(action) {
    const { data } = yield getLangs();
    yield put(actions.fulfillLangs(data, action.payload.isFilter));
  });

  yield takeLatest(actionTypes.GetLangScopes, function* getLangScopesSaga() {
    const { data } = yield getLangScopes();
    yield put(actions.fulfillLangScopes(data));
  });
}
